import {
  CompLayout,
  CompRef,
  CompStructure,
  PageRef,
  Point,
  RouterRef,
  WidgetDescriptor,
} from '@wix/document-services-types';
import {
  chainPromisesAsync,
  getInstallationPresetIdByBreakpoint,
  getSize,
} from '../utils';
import {
  AddToStageData,
  AppData,
  Container,
  UnifiedComponentsCollection,
  UnifiedPage,
  UnifiedLightbox,
  UnifiedWidget,
  WidgetAsContent,
  PageWidgetAsContent,
  ComponentType,
  MainPresets,
  DefaultPresetKeys,
  StaticContainer,
  WidgetAppearanceDescriptor,
  BuilderComponent,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorType,
  createAddUnifiedComponentsError,
  errorTypeMapping,
} from '../errors';
import { ExtendedPlatformContext } from '../../../../../types/platformApi';
import {
  addBlocksWidgetResolver,
  getBlocksInstallationSizeResolver,
  getBlocksStructure,
} from './addBlocksWidget';
import { addBuilderComponent } from './addBuilderComponent';
import {
  addCustomElement,
  getCustomElementStructure,
} from './addCustomElement';
import { Item } from '@wix/editor-platform-host-integration-apis';
import { getCloseButton } from './closeButton';
import { createBlankLightbox } from './addLightbox';
import { UNIFIED_COMPONENTS_PROTO_CHANGES } from '../constants';

export const TOKEN = 'unified-components-installation';

const addWidgetHandler = {
  [ComponentType.STUDIO_WIDGET]: addBlocksWidgetResolver,
  [ComponentType.CUSTOM_ELEMENT_WIDGET]: addCustomElement,
};

const getWidgetSizeHandler = {
  [ComponentType.STUDIO_WIDGET]: getBlocksInstallationSizeResolver,
  [ComponentType.CUSTOM_ELEMENT_WIDGET]: (
    _appData: AppData,
    widgetDefinition: UnifiedWidget,
    _preset?: MainPresets,
  ) => getSize(widgetDefinition.size),
};

const getStructureHandler = {
  [ComponentType.STUDIO_WIDGET]: (
    context: ExtendedPlatformContext,
    { appDefinitionId }: AppData,
    widgetDefinition: UnifiedWidget,
    { id: presetId, mobilePresetId }: Omit<Item, 'addToStage' | 'getStructure'>,
    widgetDescriptor: WidgetAppearanceDescriptor,
    containerLayout: CompLayout | null,
    position?: Point,
  ) =>
    getBlocksStructure(
      context,
      appDefinitionId,
      widgetDefinition,
      presetId,
      widgetDescriptor,
      containerLayout,
      position,
      mobilePresetId,
    ),
  [ComponentType.CUSTOM_ELEMENT_WIDGET]: (
    _context: ExtendedPlatformContext,
    { appDefinitionId }: AppData,
    { widgetId }: UnifiedWidget,
    { size }: Omit<Item, 'addToStage' | 'getStructure'>,
    _widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
    containerLayout: CompLayout | null,
    position?: Point,
  ) =>
    getCustomElementStructure(
      appDefinitionId,
      widgetId,
      size,
      containerLayout,
      position,
    ),
};

type AllowedWidget = keyof typeof addWidgetHandler;

type ContainerContentMap = Record<string, UnifiedWidget | BuilderComponent>;

export function getAddUnifiedComponentsMethods(
  context: ExtendedPlatformContext,
  appData: AppData,
) {
  const shouldReadDataFromNewSchema = context.experimentsAPI.enabled(
    UNIFIED_COMPONENTS_PROTO_CHANGES,
  );

  const handleAddWidgetInUnifiedPage = async (
    pageRef: PageRef,
    widgetDefinition: UnifiedWidget | BuilderComponent,
    preset: MainPresets | undefined,
  ) => {
    const isBuilderComponent = !('widgetId' in widgetDefinition);
    if (isBuilderComponent) {
      const staticContainer =
        widgetDefinition.data.installation?.staticContainer;
      if (
        staticContainer === StaticContainer.HOMEPAGE ||
        staticContainer === StaticContainer.GLOBAL_PAGE
      ) {
        return;
      }
      await addWidgetWithinNewSection(pageRef, widgetDefinition, {});
      return;
    }
    await addWidgetWithinNewSection(pageRef, widgetDefinition, {
      presetId: getInstallationPresetIdByBreakpoint({
        widgetDefinition,
        presetIdKey: DefaultPresetKeys.LARGE,
        overridePreset: preset,
        shouldReadDataFromNewSchema,
      }),
      mobilePresetId: getInstallationPresetIdByBreakpoint({
        widgetDefinition,
        presetIdKey: DefaultPresetKeys.SMALL,
        overridePreset: preset,
        shouldReadDataFromNewSchema,
      }),
    });
  };

  const handleAddWidgetInLightbox = async (
    pageRef: PageRef,
    widgetDefinition: UnifiedWidget,
    preset?: MainPresets,
  ) => {
    const lightboxContainerRef =
      context.documentServices.components.getChildren(pageRef);
    if (!lightboxContainerRef) {
      throw createAddUnifiedComponentsError(
        ErrorType.gettingLightboxContainer,
        `Could not fetch lightbox container`,
      );
    }
    if (Object.keys(getWidgetSizeHandler).includes(widgetDefinition.type)) {
      const { width, height } = await getWidgetSizeHandler[
        widgetDefinition.type as AllowedWidget
      ](appData, widgetDefinition, preset);
      context.documentServices.components.layout.update(
        lightboxContainerRef[0],
        {
          width,
          height,
        },
      );
    } else {
      const { widgetId } = widgetDefinition;
      throw createAddUnifiedComponentsError(
        ErrorType.widgetTypeNotSupported,
        `Could not fetch size for widget`,
      ).withUserComponentId(widgetId, 'unified-widget');
    }

    await addWidgetInSection(widgetDefinition, lightboxContainerRef[0], {
      presetId: getInstallationPresetIdByBreakpoint({
        widgetDefinition,
        presetIdKey: DefaultPresetKeys.LARGE,
        overridePreset: preset,
        shouldReadDataFromNewSchema,
      }),
      mobilePresetId: getInstallationPresetIdByBreakpoint({
        widgetDefinition,
        presetIdKey: DefaultPresetKeys.SMALL,
        overridePreset: preset,
        shouldReadDataFromNewSchema,
      }),
    });
  };

  const addWidgetInHandler = {
    [ComponentType.UNIFIED_PAGE]: handleAddWidgetInUnifiedPage,
    [ComponentType.UNIFIED_LIGHTBOX]: (
      pageRef: PageRef,
      widgetDefinition: UnifiedWidget | BuilderComponent,
      preset?: MainPresets,
    ) => {
      // Lightbox content supports only widgets (not builder components).
      const isBuilderComponent = !('widgetId' in widgetDefinition);
      if (isBuilderComponent) {
        return Promise.resolve();
      }
      return handleAddWidgetInLightbox(pageRef, widgetDefinition, preset);
    },
  };

  // TODO: [EP-8489] Consolidate with addPageExtension in editor-platform-private-api addPageExtension.ts
  function createBlankPage(
    container: Container,
    _context: ExtendedPlatformContext,
    _appData: AppData,
  ) {
    const pageDefinition = container.data as UnifiedPage;
    return new Promise<PageRef>((resolve, reject) => {
      const deprecatedPageUriSEO = pageDefinition.installation?.page?.slug
        ?.split('/')
        .pop();

      const pageUriSEO =
        (shouldReadDataFromNewSchema
          ? pageDefinition.editorSettings?.slug
          : undefined) ?? deprecatedPageUriSEO;
      if (!pageUriSEO) {
        // TODO: Should we create logic that define a default?
        reject(
          createAddUnifiedComponentsError(
            ErrorType.pageHaveNoSlugDefinition,
            `The page ${pageDefinition.base?.name} have no slug definition`,
          ),
        );
      }
      const definition = {
        data: {
          tpaPageId: pageDefinition.base?.id,
          pageUriSEO,
          appDefinitionId: appData.appDefinitionId,
          managingAppDefId: appData.appDefinitionId,
        },
      };

      try {
        const deprecatedAddToSiteMenu =
          pageDefinition.installation?.page?.addToSiteMenu;
        const fallbackAddToSiteMenu = deprecatedAddToSiteMenu ?? false;

        // Should use pages.add of DS instead - once studio migrate their logic to be covered in DS.
        context.platformApiMethods.document.pages
          .add(appData, TOKEN, {
            definition,
            title: pageDefinition.base?.name as string,
            shouldAddMenuItem:
              (shouldReadDataFromNewSchema
                ? pageDefinition.editorSettings?.base?.addToSiteMenu
                : undefined) ?? fallbackAddToSiteMenu,
            shouldNavigateToPage: false,
          })
          .then((pageRef) =>
            context.documentServices.waitForChangesApplied(() =>
              resolve(pageRef),
            ),
          );
      } catch (error) {
        reject(error);
      }
    });
  }

  const addContainerHandler = {
    [ComponentType.UNIFIED_LIGHTBOX]: createBlankLightbox,
    [ComponentType.UNIFIED_PAGE]: createBlankPage,
  };

  async function addPage(
    container: Container,
    content: ContainerContentMap,
  ): Promise<PageRef | undefined> {
    // TODO: Do we want to throw error or to generate alternative slug?
    try {
      const pageCompRef = await addContainerHandler[container.type](
        container,
        context,
        appData,
      );
      return await addContentToPage(container, content, pageCompRef as PageRef);
    } catch (error) {
      throw createAddUnifiedComponentsError(
        ErrorType.creatingNewPageFailed,
        `Failed to create ${container.data.base?.id} ${container.type}`,
      )
        .withUserComponentId(
          container.data.base?.id as string,
          errorTypeMapping[container.type],
        )
        .withParentError(error as Error);
    }
  }

  async function addCloseButtonIfNeeded(
    lightboxCompRef: CompRef,
    containerDefinition: UnifiedPage | UnifiedLightbox,
  ) {
    if (!(containerDefinition as UnifiedLightbox)?.closeOptions?.showXButton) {
      return;
    }

    const lightboxChildrenRef =
      context.documentServices.components.getChildren(lightboxCompRef);
    if (!lightboxChildrenRef?.length) {
      throw createAddUnifiedComponentsError(
        ErrorType.gettingLightboxContainer,
        `Could not fetch lightbox container`,
      );
    }
    const closeButtonDefinition = getCloseButton(
      context,
      lightboxChildrenRef[0],
    );
    context.platformApiMethods.document.components
      .add(appData, TOKEN, {
        componentDefinition: closeButtonDefinition,
        pageRef: lightboxChildrenRef[0],
      })
      .then((compRef) => {
        context.documentServices.waitForChangesApplied(() =>
          Promise.resolve(compRef),
        );
      });
  }

  function addContentToPage(
    container: Container,
    content: ContainerContentMap,
    pageRef: PageRef,
  ): Promise<PageRef> {
    const addWidgetAsContent = (
      widget: WidgetAsContent | PageWidgetAsContent,
    ) =>
      new Promise<void>((resolve, reject) => {
        const { widgetGuid, preset } = widget;
        const componentDefinition = content[widgetGuid as string];
        if (!componentDefinition) {
          return reject(
            createAddUnifiedComponentsError(
              ErrorType.widgetGuidNotFound,
              `Could not find widget: ${widgetGuid} to install over page: ${pageDefinition.base?.name}`,
            ).withUserComponentId(widgetGuid as string, 'unified-widget'),
          );
        }
        addWidgetInHandler[container.type](pageRef, componentDefinition, preset)
          .then(() => {
            context.documentServices.waitForChangesApplied(resolve);
          })
          .catch(reject);
      });

    const pageDefinition = container.data;
    return new Promise((resolve, reject) => {
      chainPromisesAsync(
        pageDefinition.content?.widgets ?? [],
        addWidgetAsContent,
      )
        .then(() => addCloseButtonIfNeeded(pageRef, pageDefinition))
        .then(() => resolve(pageRef))
        .catch(reject);
    });
  }

  function addWidgetWithinNewSection(
    pageRef: PageRef,
    widgetDefinition: UnifiedWidget | BuilderComponent,
    addToStageData: AddToStageData,
  ): Promise<{ widgetRef: CompRef; pageRef: PageRef }> {
    return new Promise((resolve, reject) => {
      const isBuilderComponent = !('widgetId' in widgetDefinition);
      context.platformApiMethods.editor.sections
        .addSectionToPage(
          appData,
          TOKEN,
          (newSectionRef: CompRef) => {
            if (isBuilderComponent) {
              return addBuilderInSection(widgetDefinition, newSectionRef);
            }
            return addWidgetInSection(
              widgetDefinition,
              newSectionRef,
              addToStageData,
            );
          },
          {
            pageRef,
            sectionName: isBuilderComponent
              ? widgetDefinition.name
              : widgetDefinition?.base?.name,
          },
        )
        .then((widgetRef) => {
          resolve({ widgetRef, pageRef });
        })
        .catch((error: Error) => {
          const userComponentId =
            'widgetId' in widgetDefinition
              ? widgetDefinition?.base?.id ?? widgetDefinition.widgetId
              : widgetDefinition.componentId;
          const componentType =
            'widgetId' in widgetDefinition
              ? 'unified-widget'
              : 'builder-component';
          reject(
            createAddUnifiedComponentsError(
              ErrorType.addingWidgetIntoSectionFailed,
              'failed to add widget within new section',
            )
              .withPageRef(pageRef, true)
              .withUserComponentId(userComponentId, componentType)
              .withParentError(error as Error),
          );
        });
    });
  }

  function addBuilderInSection(
    widgetDefinition: BuilderComponent,
    containerRef: CompRef,
  ): Promise<CompRef> {
    return new Promise((resolve, reject) => {
      addBuilderComponent(context, appData, widgetDefinition, containerRef)
        .then((widgetRef) => {
          context.documentServices.waitForChangesApplied(() =>
            resolve(widgetRef),
          );
        })
        .catch(reject);
    });
  }

  // TODO: Do not add widget when it installed as 'maxInstances' times - trigger installation failure instead.
  function addWidgetInSection(
    widgetDefinition: UnifiedWidget,
    containerRef: CompRef,
    addToStageData: AddToStageData,
  ): Promise<CompRef> {
    return new Promise((resolve, reject) => {
      if (Object.keys(addWidgetHandler).includes(widgetDefinition.type)) {
        addWidgetHandler[widgetDefinition.type as AllowedWidget](
          context,
          appData,
          widgetDefinition,
          containerRef,
          addToStageData,
        )
          .then((widgetRef) => {
            context.documentServices.waitForChangesApplied(() =>
              resolve(widgetRef),
            );
          })
          .catch(reject);
      } else {
        const { widgetId } = widgetDefinition;
        reject(
          createAddUnifiedComponentsError(
            ErrorType.widgetTypeNotSupported,
            `There is no implementation to support adding ${widgetDefinition.type} widget`,
          ).withUserComponentId(widgetId, 'unified-widget'),
        );
      }
    });
  }

  function getStructure(
    widgetDefinition: UnifiedWidget,
    widgetDescriptor: Pick<WidgetDescriptor, 'defaultSize' | 'presets'>,
    presetData: Omit<Item, 'addToStage' | 'getStructure'>,
    containerRef?: CompRef,
    position?: Point,
  ): CompStructure {
    const containerLayout = containerRef
      ? context.documentServices.components.layout.get(containerRef)
      : null;
    if (Object.keys(getStructureHandler).includes(widgetDefinition.type)) {
      return getStructureHandler[widgetDefinition.type as AllowedWidget](
        context,
        appData,
        widgetDefinition,
        presetData,
        widgetDescriptor,
        containerLayout,
        position,
      ) as CompStructure;
    } else {
      const { widgetId } = widgetDefinition;
      throw createAddUnifiedComponentsError(
        ErrorType.widgetTypeNotSupported,
        `There is no implementation to support getting structure of ${widgetDefinition.type} widget`,
      ).withUserComponentId(widgetId, 'unified-widget');
    }
  }

  function addWidget(pageRef: PageRef, widgetDefinition: UnifiedWidget) {
    return new Promise<{ widgetRef: CompRef; pageRef: PageRef } | void>(
      (resolve, reject) => {
        const widgetStaticContainer =
          widgetDefinition.installation?.staticContainer;
        const shouldAutoAdd = widgetDefinition.installation?.base?.autoAdd;

        if (
          shouldReadDataFromNewSchema &&
          widgetStaticContainer &&
          widgetStaticContainer !== StaticContainer.HOMEPAGE
        ) {
          return resolve();
        }

        if (
          (!shouldReadDataFromNewSchema || !widgetStaticContainer) &&
          !shouldAutoAdd
        ) {
          return resolve();
        }

        addWidgetWithinNewSection(pageRef, widgetDefinition, {
          presetId: getInstallationPresetIdByBreakpoint({
            widgetDefinition,
            shouldReadDataFromNewSchema,
          }),
          mobilePresetId: getInstallationPresetIdByBreakpoint({
            widgetDefinition,
            presetIdKey: DefaultPresetKeys.SMALL,
            shouldReadDataFromNewSchema,
          }),
        })
          .then(resolve)
          .catch(reject);
      },
    );
  }

  function waitForChanges(): Promise<void> {
    return new Promise((resolve) =>
      context.documentServices.waitForChangesApplied(resolve),
    );
  }

  async function addComponent(
    containerRef: CompRef,
    component: BuilderComponent,
  ): Promise<CompRef | undefined> {
    const installation =
      context.componentsPlatformService?.manifest?.getInstallationByComponentType?.(
        component.data.type!,
      ) ?? component.data.installation;

    const isGlobalPage =
      installation?.staticContainer === StaticContainer.GLOBAL_PAGE;
    const isHomepage =
      installation?.staticContainer === StaticContainer.HOMEPAGE;

    if (!isGlobalPage && !isHomepage) {
      return;
    }

    if (isHomepage) {
      const { widgetRef } = await addWidgetWithinNewSection(
        containerRef as PageRef,
        component,
        {},
      );
      return widgetRef;
    }

    const compRef = await addBuilderComponent(
      context,
      appData,
      component,
      containerRef,
      installation,
    );
    await waitForChanges();
    return compRef;
  }

  async function addRouter({
    appDefinitionId,
    extensionId,
    pagesToConnect,
    prefix,
  }: {
    appDefinitionId: string;
    extensionId: string;
    pagesToConnect: { pageRef: PageRef; pageRole: string }[];
    prefix: string;
  }): Promise<RouterRef> {
    let routerRef: RouterRef;

    try {
      routerRef = await context.documentServices.routers.add({
        appDefinitionId,
        extensionId,
        config: '',
        prefix,
      });
    } catch (error) {
      throw createAddUnifiedComponentsError(
        ErrorType.addingRouterFailed,
        `Failed to add router ${prefix}`,
      )
        .withUserComponentId(extensionId, 'app-router')
        .withParentError(error as Error);
    }

    for (const { pageRef, pageRole } of pagesToConnect) {
      try {
        await context.documentServices.routers.pages.connect(
          routerRef,
          pageRef,
          [pageRole],
          {},
        );
      } catch (error) {
        throw createAddUnifiedComponentsError(
          ErrorType.connectPageToRouterFailed,
          `Failed to connect page ${pageRef.id!} to router`,
        )
          .withUserComponentId(pageRef.id!, 'unified-page')
          .withParentError(error as Error);
      }
    }

    await waitForChanges();
    return routerRef;
  }

  return {
    addComponent,
    addPage,
    addRouter,
    addWidget,
    addWidgetInSection,
    getStructure,
  };
}
